/**
 * Notification Utilities for enhanced UX
 * Includes sound effects and haptic feedback for Firebase realtime updates
 */

// Sound effects using Web Audio API
class SoundManager {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    this.init();
  }

  init() {
    try {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
    } catch (error) {
      console.warn("Web Audio API not supported:", error);
      this.isEnabled = false;
    }
  }

  // Generate notification sounds
  playNotificationSound(type = "progress") {
    if (!this.isEnabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Different frequencies for different notification types
      const frequencies = {
        progress: [440, 550], // A4 to C#5
        completed: [523, 659, 784], // C5, E5, G5 (major chord)
        error: [200, 150], // Low frequency for error
        step: [800], // High pitch for step completion
      };

      const freq = frequencies[type] || frequencies.progress;

      // Play sequence of tones
      freq.forEach((frequency, index) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.audioContext.destination);

        osc.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
        osc.type = type === "error" ? "sawtooth" : "sine";

        // Volume envelope
        gain.gain.setValueAtTime(0, this.audioContext.currentTime);
        gain.gain.linearRampToValueAtTime(
          0.1,
          this.audioContext.currentTime + 0.01
        );
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          this.audioContext.currentTime + 0.2
        );

        osc.start(this.audioContext.currentTime + index * 0.1);
        osc.stop(this.audioContext.currentTime + 0.2 + index * 0.1);
      });
    } catch (error) {
      console.warn("Error playing notification sound:", error);
    }
  }

  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Haptic feedback using Vibration API
class HapticManager {
  constructor() {
    this.isSupported = "vibrate" in navigator;
    this.isEnabled = true;
  }

  vibrate(pattern = [200]) {
    if (!this.isSupported || !this.isEnabled) return;

    try {
      navigator.vibrate(pattern);
    } catch (error) {
      console.warn("Error with haptic feedback:", error);
    }
  }

  // Predefined vibration patterns
  progressStep() {
    this.vibrate([50]); // Short vibration for step completion
  }

  completed() {
    this.vibrate([100, 100, 100]); // Three pulses for completion
  }

  error() {
    this.vibrate([200, 100, 200]); // Strong-weak-strong for error
  }

  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Global instances
const soundManager = new SoundManager();
const hapticManager = new HapticManager();

// Enhanced notification functions
export const playProgressSound = () => {
  soundManager.playNotificationSound("progress");
};

export const playStepCompletedSound = () => {
  soundManager.playNotificationSound("step");
  hapticManager.progressStep();
};

export const playCompletedSound = () => {
  soundManager.playNotificationSound("completed");
  hapticManager.completed();
};

export const playErrorSound = () => {
  soundManager.playNotificationSound("error");
  hapticManager.error();
};

// Settings management
export const setNotificationsEnabled = (soundEnabled, hapticEnabled) => {
  soundManager.setEnabled(soundEnabled);
  hapticManager.setEnabled(hapticEnabled);
};

// Browser notification support
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.warn("This browser does not support notifications");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

export const showBrowserNotification = (title, options = {}) => {
  if (Notification.permission !== "granted") return;

  const defaultOptions = {
    icon: "/favicon.ico",
    badge: "/favicon.ico",
    tag: "aiprice-notification",
    requireInteraction: false,
    ...options,
  };

  try {
    const notification = new Notification(title, defaultOptions);

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  } catch (error) {
    console.warn("Error showing browser notification:", error);
  }
};

// Comprehensive notification for Firebase progress updates
export const notifyProgressUpdate = (
  step,
  isCompleted = false,
  isError = false
) => {
  if (isError) {
    playErrorSound();
    showBrowserNotification("❌ Lỗi phân tích", {
      body: "Có lỗi xảy ra trong quá trình phân tích. Vui lòng thử lại.",
      icon: "/favicon.ico",
    });
  } else if (isCompleted) {
    playCompletedSound();
    showBrowserNotification("✅ Phân tích hoàn thành!", {
      body: "Kết quả định giá đã sẵn sàng. Click để xem chi tiết.",
      icon: "/favicon.ico",
      requireInteraction: true,
    });
  } else {
    playStepCompletedSound();
  }
};

// Initialize notifications when user interacts with page
export const initializeNotifications = async () => {
  // Request permission for browser notifications
  await requestNotificationPermission();

  // Initialize audio context on user interaction (required by browsers)
  const initAudio = () => {
    if (
      soundManager.audioContext &&
      soundManager.audioContext.state === "suspended"
    ) {
      soundManager.audioContext.resume();
    }
    document.removeEventListener("click", initAudio);
    document.removeEventListener("keydown", initAudio);
  };

  document.addEventListener("click", initAudio);
  document.addEventListener("keydown", initAudio);
};

// Check if notifications are supported
export const getNotificationSupport = () => {
  return {
    browser: "Notification" in window,
    vibration: "vibrate" in navigator,
    audio: !!(window.AudioContext || window.webkitAudioContext),
  };
};

const notificationUtils = {
  playProgressSound,
  playStepCompletedSound,
  playCompletedSound,
  playErrorSound,
  setNotificationsEnabled,
  notifyProgressUpdate,
  initializeNotifications,
  getNotificationSupport,
};

export default notificationUtils;
